/**
 * **P-320** — what a click inside the Item cell does, as a function of where it lands.
 *
 * Captured on monday's sandbox board before this was built
 * (`discovery/ui-reference/board-view/name-cell/`, `tools/browser/capture-round11m.mjs`,
 * board 18430299563, item 13009142461, 1440 × 900). The row's sticky region is x 391-791; the
 * subitems chevron ends at 456; the name's own span — `span.ds-text-component-content-text`,
 * which **hugs its ink**, 141 px wide for *"Item 26 · Working on it"* — runs x 464-605; then
 * 90 px of empty cell; then the open-item button at 695-717 and the conversation cell at
 * 725-790. Four single clicks on that one row, each from a clean board:
 *
 * | click | x | what monday did |
 * |---|---|---|
 * | on the name text | 484 | **no card**, URL unchanged, the name's input takes focus |
 * | 40 px right of the text end | 645 | `/pulses/13009142461`, card open, tab **`Updates / 1`** |
 * | 14 px left of the open button | 681 | the same |
 * | the conversation bubble | 758 | the same |
 *
 * So the empty area of the Item cell is the conversation bubble's gesture, and the text is the
 * cell's own. That is the whole card, and this module is the rule both tables ask — `GridRow`
 * for an item and `SubitemRow` for a subitem — so there is one answer and one place to fix it.
 *
 * Two things make it work in the DOM:
 *
 *   · `table.css` gives the **name** cell's `.ez-cell__text` `width: auto; max-width: 100%`, so
 *     the span hugs its ink the way monday's does. With the shared `width: 100%` every click in
 *     the cell hit the text and there was no empty area to speak of — the defect P-320 reports.
 *   · the gesture is read on `mousedown` **capture** so the cell underneath never takes focus
 *     (monday's click leaves no selection behind), and acted on at `click`, so a row *dragged*
 *     from its empty area still drags and opens nothing. The capture handler only stops
 *     propagation — it never calls `preventDefault`, which would kill `dragstart`.
 */
import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * Anything that already says what it does: the row menu, the checkbox, the subitems chevron,
 * the `+`, the open-item arrow, the conversation bubble, a link, an open editor's input. A
 * control is never the open gesture, whatever it sits on top of.
 *
 * `.ez-row__subitems-count` is in this list for a different reason: monday's own count badge was
 * **not** captured (round 11m measured a row whose subitems are collapsed behind a chevron with
 * no badge), so its click behaviour is unknown and the conservative reading — CLAUDE.md rule 3 —
 * is to leave it exactly as it was rather than make it open the card. `docs/open-questions.md`
 * carries the question.
 */
const NOT_THE_EMPTY_AREA = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="checkbox"]',
  '[contenteditable="true"]',
  '.ez-row__subitems-count',
].join(', ');

/** The name's text, which keeps the cell's own "first click selects, second edits" rule. */
const NAME_TEXT = '.ez-cell__text';

/**
 * Does this event land **beside** the name text, in the Item cell's empty area?
 *
 * `currentTarget` must be the name-cell wrapper (`.ez-row__name` / `.ez-subrow__name`).
 */
export function isNameCellOpenGesture(event: ReactMouseEvent<HTMLElement>): boolean {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  const wrapper = event.currentTarget;
  if (!wrapper.contains(target)) return false;

  // While a cell of this row is being edited, the editor owns every click inside it (P-019).
  if (wrapper.querySelector('.ez-cell[data-editing]') !== null) return false;

  if (target.closest(NOT_THE_EMPTY_AREA) !== null) return false;
  if (target.closest(NAME_TEXT) !== null) return false;

  /*
   * Right of the text, not left of it. The cell has 8 px of padding before the name and monday's
   * behaviour **there** was never captured, so that strip keeps doing what it does today. When
   * the box cannot be measured — jsdom reports zeroes — the hit test above is the whole answer.
   */
  const text = wrapper.querySelector(NAME_TEXT);
  if (text !== null) {
    const box = text.getBoundingClientRect();
    if (box.width > 0 && event.clientX < box.right) return false;
  }
  return true;
}
